import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import ContentCopy from '@mui/icons-material/ContentCopy';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import {
  CONFIRMATION,
  COPIED_TO_CLIPBOARD,
  EMAIL_LINK,
  EMAIL_TEMPLATE_BODY_$,
  EMAIL_TEMPLATE_SUBJECT,
  SEND,
} from '../../constants/strings';
import { FMP_BASE_URL } from '../../constants/env';
import { shareParcelSchema, validateWithJoi } from '../../helpers/validators';
import { FormInput } from '../FormInput';

const ShareModal = ({ open, onClose, parcel = {} }) => {
  const theme = useTheme();
  const { parcelCode } = parcel;
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const link = parcelCode ? `${FMP_BASE_URL}/parcels/${parcelCode}` : '';
  const hideCopiedMessageTimeout = 2000;

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, hideCopiedMessageTimeout);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          position: 'relative',
          m: { sm: '10% auto' },
          borderRadius: 1,
          maxWidth: { sm: 600 },
          width: { sm: '100%', md: '328px' },
          height: {
            xs: '100%',
            sm: 'auto',
          },
          maxHeight: {
            md: '55%',
          },
          overflowY: {
            xs: 'scroll',
            sm: 'auto',
            md: 'hidden',
          },
        }}
        role='dialog'
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Typography variant='h3' sx={{ py: 1 }}>
            {CONFIRMATION.SHARE_MODAL_TITLE}
          </Typography>
          <IconButton
            aria-label='close'
            sx={{ width: '35px', height: '35px' }}
            onClick={onClose}
          >
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <Form
          validate={values => validateWithJoi(values, shareParcelSchema)}
          onSubmit={() => {}}
          render={({ invalid }) => (
            <Box
              sx={{
                p: 2,
                pt: 0,
                display: 'flex',
                justifyContent: 'space-between',
                height: '320px',
                overflow: 'auto',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ my: 1 }}>
                <Typography variant='body2'>
                  {CONFIRMATION.COPY_LINK}
                </Typography>
                <TextField
                  fullWidth
                  style={{
                    background: theme.vars.palette.primary.pageBackground,
                  }}
                  value={link}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={handleCopy}
                          edge='end'
                          aria-label='copy link'
                        >
                          <ContentCopy />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mt: 2 }}
                />
                {copied && (
                  <Typography
                    variant='body2'
                    color='success.main'
                    sx={{ mt: 1 }}
                  >
                    {COPIED_TO_CLIPBOARD}
                  </Typography>
                )}
              </Box>

              <Divider />
              <Box sx={{ my: 1 }}>
                <Typography variant='body2'>{EMAIL_LINK}</Typography>
                <FormInput
                  name='email'
                  fullWidth
                  placeholder='Enter Email address '
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  sx={{ mt: 2 }}
                  maxLength={100}
                />
              </Box>
              <Button
                sx={{ my: 1 }}
                variant='contained'
                disabled={invalid}
                component='a'
                href={`mailto:${email}?subject=${encodeURIComponent(
                  EMAIL_TEMPLATE_SUBJECT
                )}&body=${encodeURIComponent(EMAIL_TEMPLATE_BODY_$(link))}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                {SEND}
              </Button>
            </Box>
          )}
        />
      </Box>
    </Modal>
  );
};

ShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  parcel: PropTypes.shape({
    parcelNumber: PropTypes.string,
  }),
};

export default ShareModal;
